/**
 * Pendientes por vehículo: qué quedó sin resolver y qué se venció.
 *
 * Es la lista para trabajar cuando uno quiere (no interrumpe): el aviso que
 * sirve de verdad es el que salta con el auto en el taller. Acá se ve el
 * acumulado, se busca por patente o cliente y se acciona punto por punto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ExclamationTriangleIcon, ClockIcon, MagnifyingGlassIcon, Cog6ToothIcon,
  EllipsisHorizontalIcon, TruckIcon
} from '@heroicons/react/24/outline';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Tip } from '../components/ui/tip';
import { Toggle } from '../components/ui/toggle';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useAuth } from '../contexts/AuthContext';

interface Punto {
  pointKey: string;
  label: string;
  situacion: 'vencido' | 'pendiente' | 'por_vencer';
  motivo?: string | null;
  checkedAt?: string | null;
  doneAt?: string | null;
}

interface Contacto {
  tipo: 'titular' | 'empresa' | 'vehiculo';
  etiqueta: string;
  nombre?: string | null;
  telefono: string;
}

interface VehiculoFila {
  vehicleId: string;
  licensePlate?: string | null;
  vehiculo?: string;
  clientId?: string | null;
  clientName?: string;
  contactos?: Contacto[];
  contactado?: boolean;
  contactedAt?: string | null;
  contactedTo?: string | null;
  kmActual?: number | null;
  puntos: Punto[];
}

// 'YYYY-MM-DD' → '05/09/2026' (sin pasar por Date, así no se corre de día)
const diaCorto = (d?: string | null) => {
  const [y, m, dd] = String(d || '').slice(0, 10).split('-');
  return y && m && dd ? `${dd}/${m}/${y}` : '';
};

// De cuándo es el dato: el pendiente sale del checklist; el vencido, del último trabajo hecho
const fechaDePunto = (p: Punto) => {
  if (p.situacion === 'pendiente') return p.checkedAt ? `revisado el ${diaCorto(p.checkedAt)}` : '';
  return p.doneAt ? `hecho el ${diaCorto(p.doneAt)}` : '';
};

// Días que se le dan al cliente para contestar antes de sugerir el reintento
const DIAS_DE_GRACIA = 21;

const fechaCorta = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`;
};

const diasDesde = (iso?: string | null) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86400000);
};

const CHIP: Record<string, string> = {
  vencido: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
  pendiente: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  por_vencer: 'border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400'
};
const CHIP_LABEL: Record<string, string> = {
  vencido: 'Vencido', pendiente: 'Pendiente', por_vencer: 'Por vencer'
};

export default function ServiceStatus() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const puedeConfigurar = hasPermission('manage_settings');

  const [datos, setDatos] = useState<{ vehiculos: VehiculoFila[]; vencidos: number; pendientes: number; porVencer: number; totalVehiculos: number; contactados: number } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<'todos' | 'vencido' | 'pendiente' | 'por_vencer' | 'sin_contactar'>('todos');
  const [configAbierta, setConfigAbierta] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axios.get('/api/service-status/resumen?limit=300');
      setDatos(res.data);
    } catch {
      toast.error('No se pudo cargar el listado');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (v: VehiculoFila, p: Punto, modo: string, meses?: number) => {
    try {
      const url = modo === 'hecho-afuera'
        ? `/api/service-status/vehicle/${v.vehicleId}/${p.pointKey}/hecho-afuera`
        : `/api/service-status/vehicle/${v.vehicleId}/${p.pointKey}/snooze`;
      await axios.post(url, modo === 'hecho-afuera' ? { km: v.kmActual } : { modo, meses });
      // Sacamos el punto de la vista sin recargar todo (se veía como un refresh)
      setDatos((prev) => {
        if (!prev) return prev;
        const vehiculos = prev.vehiculos
          .map((x) => x.vehicleId === v.vehicleId
            ? { ...x, puntos: x.puntos.filter((y) => y.pointKey !== p.pointKey) }
            : x)
          .filter((x) => x.puntos.length > 0);
        return { ...prev, vehiculos, totalVehiculos: vehiculos.length };
      });
      toast.success(`${p.label}: listo`);
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  // Abre WhatsApp con el mensaje ya escrito y deja registrado a quién se le
  // escribió. Va por el WhatsApp propio, no por la API: queda como una
  // conversación normal y se puede editar antes de mandar.
  const hablarPorWhatsApp = async (v: VehiculoFila, contacto: Contacto) => {
    const digits = String(contacto.telefono || '').replace(/\D/g, '');
    if (!digits) {
      toast.error('Ese contacto no tiene teléfono válido');
      return;
    }
    const nombre = (contacto.nombre || v.clientName || '').split(' ')[0] || '';
    const auto = [v.vehiculo, v.licensePlate ? `(${v.licensePlate})` : ''].filter(Boolean).join(' ');
    const items = v.puntos.slice(0, 4).map((p) => `• ${p.label}`).join('\n');
    const resto = v.puntos.length > 4 ? `\n• y ${v.puntos.length - 4} más` : '';

    // Un vencimiento y un pendiente no se cuentan igual: del pendiente el
    // cliente ya sabe (se lo dijimos y no lo hizo), del vencimiento no —
    // simplemente se cumplió el plazo.
    const soloVencidos = v.puntos.every((p) => p.situacion === 'vencido' || p.situacion === 'por_vencer');
    const cuerpo = soloVencidos
      ? `Ya le toca el service a tu ${auto}:\n${items}${resto}`
      : `De la última revisión de tu ${auto} nos quedó pendiente:\n${items}${resto}`;
    const texto =
      `Hola ${nombre}! Te escribimos de ROCAM.\n\n${cuerpo}\n\n` +
      `¿Querés que te lo presupuestemos o te agendamos un turno?`;

    const numero = digits.startsWith('549') ? digits : `549${digits}`;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(texto)}`, '_blank');

    const quien = [contacto.etiqueta, contacto.nombre].filter(Boolean).join(': ');
    try {
      await axios.post(`/api/service-status/vehicle/${v.vehicleId}/contactado`, {
        pointKeys: v.puntos.map((p) => p.pointKey),
        destinatario: quien
      });
      const ahora = new Date().toISOString();
      setDatos((prev) => prev ? {
        ...prev,
        contactados: prev.contactados + (v.contactado ? 0 : 1),
        vehiculos: prev.vehiculos.map((x) => x.vehicleId === v.vehicleId
          ? { ...x, contactado: true, contactedAt: ahora, contactedTo: quien }
          : x)
      } : prev);
    } catch {
      toast.error('Se abrió el chat pero no se pudo registrar el contacto');
    }
  };

  const filtradas = useMemo(() => {
    if (!datos) return [];
    const q = busqueda.trim().toLowerCase();
    const porSituacion = ['vencido', 'pendiente', 'por_vencer'].includes(filtro);
    return datos.vehiculos
      .filter((v) => filtro !== 'sin_contactar' || !v.contactado)
      .map((v) => ({
        ...v,
        puntos: porSituacion ? v.puntos.filter((p) => p.situacion === filtro) : v.puntos
      }))
      .filter((v) => v.puntos.length > 0)
      .filter((v) => !q ||
        String(v.licensePlate || '').toLowerCase().includes(q) ||
        String(v.clientName || '').toLowerCase().includes(q) ||
        String(v.vehiculo || '').toLowerCase().includes(q) ||
        v.puntos.some((p) => p.label.toLowerCase().includes(q)));
  }, [datos, busqueda, filtro]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Pendientes por vehículo</h1>
          <p className="text-sm text-muted-foreground">
            Lo que quedó sin resolver del checklist y lo que cumplió su tiempo o kilometraje
          </p>
        </div>
        {puedeConfigurar && (
          <Button variant="outline" size="sm" className="h-8 w-fit" onClick={() => setConfigAbierta(true)}>
            <Cog6ToothIcon className="mr-1.5 h-4 w-4" />
            Intervalos
          </Button>
        )}
      </div>

      {/* Contadores: además de informar, filtran */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {([
          ['todos', 'Vehículos', datos?.totalVehiculos ?? 0, 'text-foreground'],
          ['vencido', 'Vencidos', datos?.vencidos ?? 0, 'text-red-600 dark:text-red-400'],
          ['pendiente', 'Pendientes', datos?.pendientes ?? 0, 'text-amber-600 dark:text-amber-400'],
          ['por_vencer', 'Por vencer', datos?.porVencer ?? 0, 'text-sky-600 dark:text-sky-400'],
          ['sin_contactar', 'Sin contactar', (datos?.totalVehiculos ?? 0) - (datos?.contactados ?? 0), 'text-foreground']
        ] as const).map(([key, label, valor, color]) => (
          <button
            key={key}
            onClick={() => setFiltro(key as any)}
            className={`rounded-lg border bg-card px-4 py-3 text-left transition-colors hover:bg-accent ${filtro === key ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
          >
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={`text-2xl font-semibold ${color}`}>{valor}</p>
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por patente, cliente o punto..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="h-8 pl-8"
        />
      </div>

      {cargando ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : filtradas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center">
          <TruckIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground opacity-30" />
          <p className="text-sm text-muted-foreground">
            {busqueda || filtro !== 'todos' ? 'Nada con ese filtro' : 'No hay nada pendiente'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtradas.map((v) => (
            <div key={v.vehicleId} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{v.clientName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    <span className="font-medium text-foreground/80">{v.licensePlate || 'Sin patente'}</span>
                    {v.vehiculo ? ` · ${v.vehiculo}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {v.kmActual ? (
                    <span className="text-xs text-muted-foreground">{v.kmActual.toLocaleString('es-AR')} km</span>
                  ) : null}
                  <BotonWhatsApp vehiculo={v} onEnviar={hablarPorWhatsApp} />
                  {v.clientId && (
                    <Tip label="Abrir el cliente">
                      <button
                        onClick={() => navigate(`/clients/${v.clientId}`)}
                        className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        Ver cliente
                      </button>
                    </Tip>
                  )}
                </div>
              </div>
              <ul className="divide-y divide-border/60">
                {v.puntos.map((p) => (
                  <li key={p.pointKey} className="flex items-center gap-2 px-3 py-2">
                    <span className={`shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${CHIP[p.situacion]}`}>
                      {p.situacion === 'por_vencer'
                        ? <ClockIcon className="h-3.5 w-3.5" />
                        : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                      <span className="cq-hide-narrow">{CHIP_LABEL[p.situacion]}</span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">{p.label}</p>
                      {(p.motivo || fechaDePunto(p)) && (
                        <p className="truncate text-xs text-muted-foreground">
                          {[p.motivo, fechaDePunto(p)].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                          <EllipsisHorizontalIcon className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {/* Solo para trabajo hecho AFUERA: lo que se hace acá lo
                            marca el remito al emitirse, sin que nadie toque nada. */}
                        <DropdownMenuItem onClick={() => accion(v, p, 'hecho-afuera')}>
                          Lo hizo en otro taller
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => accion(v, p, 'posponer', 3)}>Posponer 3 meses</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => accion(v, p, 'posponer', 6)}>Posponer 6 meses</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => accion(v, p, 'dismiss')} className="text-destructive">
                          No insistir más
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <IntervalosDialog open={configAbierta} onClose={() => { setConfigAbierta(false); cargar(); }} />
    </div>
  );
}

/**
 * Botón de WhatsApp que queda marcado una vez usado, con la fecha en el tooltip.
 * Si el vehículo tiene más de un contacto abre un menú para elegir: el chofer
 * maneja el auto pero no decide qué hacerle, así que nunca es el primero.
 */
function BotonWhatsApp({
  vehiculo, onEnviar
}: { vehiculo: VehiculoFila; onEnviar: (v: VehiculoFila, c: Contacto) => void }) {
  const contactos = vehiculo.contactos || [];
  const dias = diasDesde(vehiculo.contactedAt);
  const vencida = vehiculo.contactado && dias != null && dias > DIAS_DE_GRACIA;

  const label = !contactos.length
    ? 'Sin teléfono cargado'
    : vehiculo.contactado
      ? `Contactado el ${fechaCorta(vehiculo.contactedAt)}${vehiculo.contactedTo ? ` — ${vehiculo.contactedTo}` : ''}` +
        (vencida ? ` · sin respuesta hace ${dias} días, conviene reintentar` : '')
      : contactos.length > 1
        ? 'Elegir a quién escribirle'
        : `Escribirle a ${contactos[0].nombre || 'el cliente'}`;

  const boton = (
    <Toggle
      variant="outline"
      size="sm"
      pressed={!!vehiculo.contactado}
      disabled={!contactos.length}
      onPressedChange={() => { if (contactos.length === 1) onEnviar(vehiculo, contactos[0]); }}
      aria-label={label}
      className={`h-7 gap-1 px-2 text-xs ${vencida
        ? 'data-[state=on]:border-amber-300 data-[state=on]:bg-amber-100 data-[state=on]:text-amber-700 dark:data-[state=on]:bg-amber-500/15 dark:data-[state=on]:text-amber-400'
        : 'data-[state=on]:border-green-300 data-[state=on]:bg-green-100 data-[state=on]:text-green-600 dark:data-[state=on]:bg-green-500/15 dark:data-[state=on]:text-green-400'}`}
    >
      <WhatsAppIcon className="h-3.5 w-3.5" />
      {vehiculo.contactado ? fechaCorta(vehiculo.contactedAt) : 'WhatsApp'}
    </Toggle>
  );

  // Con un solo contacto el botón manda directo; con varios abre el menú.
  // El Tip va SIEMPRE por fuera del trigger del dropdown (adentro lo rompe).
  if (contactos.length <= 1) return <Tip label={label}>{boton}</Tip>;

  return (
    <DropdownMenu>
      <Tip label={label}>
        <DropdownMenuTrigger asChild>{boton}</DropdownMenuTrigger>
      </Tip>
      <DropdownMenuContent align="end" className="w-64">
        {contactos.map((c) => (
          <DropdownMenuItem key={c.tipo} onClick={() => onEnviar(vehiculo, c)} className="flex-col items-start gap-0">
            <span className="text-sm font-medium">{c.nombre || c.etiqueta}</span>
            <span className="text-xs text-muted-foreground">{c.etiqueta} · {c.telefono}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Cada cuánto vuelve a tocar cada punto. Vacío = ese punto no vence nunca. */
function IntervalosDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [puntos, setPuntos] = useState<Array<{ key: string; label: string; seccion: string }>>([]);
  const [valores, setValores] = useState<Record<string, { meses: string; km: string }>>({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get('/api/service-status/intervals').then((res) => {
      setPuntos(res.data.puntos || []);
      const v: Record<string, { meses: string; km: string }> = {};
      for (const p of res.data.puntos || []) {
        const iv = res.data.intervals?.[p.key];
        v[p.key] = { meses: iv?.meses ? String(iv.meses) : '', km: iv?.km ? String(iv.km) : '' };
      }
      setValores(v);
    }).catch(() => toast.error('No se pudieron cargar los intervalos'));
  }, [open]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const intervals: Record<string, { meses: number | null; km: number | null }> = {};
      for (const [key, v] of Object.entries(valores)) {
        const meses = Number(v.meses) || null;
        const km = Number(v.km) || null;
        if (meses || km) intervals[key] = { meses, km };
      }
      await axios.put('/api/service-status/intervals', { intervals });
      toast.success('Intervalos guardados');
      onClose();
    } catch {
      toast.error('No se pudieron guardar');
    } finally {
      setGuardando(false);
    }
  };

  const porSeccion = useMemo(() => {
    const orden: string[] = [];
    const m: Record<string, typeof puntos> = {};
    for (const p of puntos) {
      if (!m[p.seccion]) { m[p.seccion] = []; orden.push(p.seccion); }
      m[p.seccion].push(p);
    }
    return orden.map((s) => [s, m[s]] as [string, typeof puntos]);
  }, [puntos]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[88vh] w-[96vw] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Cada cuánto vuelve a tocar</DialogTitle>
          <DialogDescription>
            Vence por lo que llegue primero de los dos. Un punto sin nada cargado no vence nunca:
            solo se persigue si quedó en amarillo o rojo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {porSeccion.map(([seccion, ps]) => (
            <div key={seccion} className="mb-4">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{seccion}</p>
              <div className="space-y-1">
                {ps.map((p) => (
                  <div key={p.key} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm">{p.label}</span>
                    <Input
                      inputMode="numeric"
                      placeholder="meses"
                      value={valores[p.key]?.meses ?? ''}
                      onChange={(e) => setValores((v) => ({ ...v, [p.key]: { ...v[p.key], meses: e.target.value.replace(/\D/g, '') } }))}
                      className="h-8 w-20 text-right"
                    />
                    <Input
                      inputMode="numeric"
                      placeholder="km"
                      value={valores[p.key]?.km ?? ''}
                      onChange={(e) => setValores((v) => ({ ...v, [p.key]: { ...v[p.key], km: e.target.value.replace(/\D/g, '') } }))}
                      className="h-8 w-24 text-right"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
